import React, { useState, useCallback, useEffect, forwardRef, createRef } from 'react';
import { NavLink } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { classNames } from 'primereact/utils';
import { Ripple } from 'primereact/ripple';
import './App.scss';

const AppSubmenu = forwardRef((props, ref) => {
    const [activeIndex, setActiveIndex] = useState(null);

    const isMobile = useCallback(() => window.innerWidth <= 1025, []);
    const isSlim = useCallback(() => props.menuMode === 'slim', [props.menuMode]);
    const isHorizontal = useCallback(() => props.menuMode === 'horizontal', [props.menuMode]);

    const onMenuItemClick = (event, item, index) => {
        if (item.disabled) {
            event.preventDefault();
            return;
        }

        // execute command
        if (item.command) {
            item.command({ originalEvent: event, item });
            event.preventDefault();
        }

        // toggle submenu
        if (item.items) {
            setActiveIndex(activeIndex === index ? null : index);
            event.preventDefault();
        } else {
            if (!props.root) {
                props.onMenuitemClick({ originalEvent: event, item });
            }
        }

        // root menu click toggles menuActive
        if (props.root) {
            props.onRootMenuitemClick({ originalEvent: event, item });
        }
    };

    const onMenuItemMouseEnter = (index) => {
        if (props.root && props.menuActive && (isHorizontal() || isSlim()) && !isMobile()) {
            setActiveIndex(index);
        }
    };

    const visible = (item) => typeof item.visible === 'function' ? item.visible() : item.visible !== false;

    const getLink = (item, index) => {
        const content = (
            <>
                {item.icon && <i className={classNames('layout-menuitem-icon', item.icon)}></i>}
                <span className="layout-menuitem-text">{item.label}</span>
                {item.items && <i className="pi pi-fw pi-angle-down menuitem-toggle-icon"></i>}
                {item.badge && <span className="menuitem-badge">{item.badge}</span>}
                <Ripple />
            </>
        );

        const commonProps = {
            style: item.style,
            className: classNames(item.className, 'p-ripple', { 'p-disabled': item.disabled }),
            target: item.target,
            onClick: (e) => onMenuItemClick(e, item, index),
            onMouseEnter: () => onMenuItemMouseEnter(index)
        };

        if (item.url) return <a href={item.url} rel="noopener noreferrer" {...commonProps}>{content}</a>;
        if (!item.to) return <button type="button" {...commonProps}>{content}</button>;

        return (
            <NavLink
                to={item.to}
                {...commonProps}
                className={({ isActive }) => classNames(commonProps.className, isActive ? 'router-link-active' : undefined)}
            >
                {content}
            </NavLink>
        );
    };

    const isMenuActive = (index) => activeIndex === index;

    const getItems = () => {
        const transitionTimeout = { enter: 400, exit: 300 };

        return props.items.map((item, i) => {
            if (!visible(item)) return null;

            if (item.separator) return <li key={`separator-${i}`} className="menu-separator" style={item.style} role="separator"></li>;

            const submenuRef = createRef();
            const menuitemClassName = classNames({
                'active-menuitem': isMenuActive(i) && !item.disabled,
                'layout-root-menuitem': props.root
            });

            return (
                <li key={item.label || i} className={menuitemClassName} role="menuitem">
                    {props.root ? (
                        <div
                            className="layout-menuitem-root-text"
                            style={{ cursor: 'pointer', textTransform: 'uppercase' }}
                            onClick={(e) => onMenuItemClick(e, item, i)}
                        >
                            {item.icon && <i className={item.icon}></i>}
                            <span>{item.label}</span>
                            {item.items && <i className="pi pi-fw pi-angle-down menuitem-toggle-icon"></i>}
                        </div>
                    ) : (
                        getLink(item, i)
                    )}

                    {item.items && (
                        <CSSTransition
                            nodeRef={submenuRef}
                            classNames="layout-submenu-container"
                            timeout={transitionTimeout}
                            in={isMenuActive(i)}
                            unmountOnExit
                        >
                            <AppSubmenu
                                ref={submenuRef}
                                items={item.items}
                                menuActive={props.menuActive}
                                menuMode={props.menuMode}
                                onMenuitemClick={props.onMenuitemClick}
                                onRootMenuitemClick={props.onRootMenuitemClick}
                                className="layout-submenu"
                            />
                        </CSSTransition>
                    )}
                </li>
            );
        });
    };

    useEffect(() => {
        if (!props.menuActive && (isSlim() || isHorizontal()) && !isMobile()) {
            setActiveIndex(null);
        }
    }, [props.menuActive, isSlim, isHorizontal, isMobile]);

    if (!props.items) return null;

    return (
        <ul ref={ref} className={props.className} role="menu">
            {getItems()}
        </ul>
    );
});

export default AppSubmenu;
